import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Users, Star, Sparkles, Phone, Leaf, Drumstick, Layers, BadgeCheck, Eye, Mail } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { CAT, CAT_LABEL as LABELS, catLabel, priceOf, hasCapacity, isFood } from "@/lib/categories";

export const CAT_LABEL = LABELS;

/** Image that falls back to the category photo, then to a branded tile, instead of a broken icon. */
export function SmartImg({ src, alt, category, className = "", icon = true }) {
  const [step, setStep] = useState(0);
  const own = mediaUrl(src);
  const cat = CAT[category]?.img;
  const url = step === 0 ? (own || cat) : step === 1 ? cat : null;
  if (!url) {
    const I = CAT[category]?.Icon || Sparkles;
    return <div className={`flex items-center justify-center bg-[color:var(--maroon-2)] rings ${className}`} role="img" aria-label={alt}>{icon && <I className="w-10 h-10 text-[color:var(--gold-2)]"/>}</div>;
  }
  return <img src={url} alt={alt} loading="lazy" onError={() => setStep((s) => (s === 0 && own && cat ? 1 : 2))} className={className}/>;
}

export default function VenueCard({ v, compact = false, testIdPrefix = "venue" }) {
  const p = priceOf(v);
  const cap = hasCapacity(v.category);
  return (
    <motion.div className="h-full" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}>
      <Link to={`/property/${v.id}`} className="card-luxury overflow-hidden group h-full flex flex-col" data-testid={`${testIdPrefix}-card-${v.id}`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-[color:var(--ivory)] shrink-0">
          <CardImage v={v}/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"/>
          {v.highlighted && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full text-[color:var(--maroon-2)] bg-[color:var(--gold-2)] shadow font-bold">
              <Sparkles className="w-3 h-3"/> Premium
            </span>
          )}
          <span className="absolute top-3 right-3 text-[12px] px-2.5 py-1 rounded-full bg-white/95 text-[color:var(--maroon)] font-semibold">{catLabel(v.category)}</span>
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2 text-white">
            <div className="text-[12px] inline-flex items-center gap-1 bg-black/35 backdrop-blur px-2 py-1 rounded-full truncate"><MapPin className="w-3 h-3 shrink-0"/> <span className="truncate">{v.address || v.city}</span></div>
            {v.rating > 0 && <div className="text-[12px] inline-flex items-center gap-1 bg-black/35 backdrop-blur px-2 py-1 rounded-full shrink-0"><Star className="w-3 h-3 fill-[color:var(--gold-2)] text-[color:var(--gold-2)]"/> {v.rating} <span className="opacity-70">({v.reviews_count || 0})</span></div>}
          </div>
        </div>
        <div className={`p-5 flex-1 flex flex-col ${compact ? "gap-1.5" : "gap-3"}`}>
          <div>
            <h3 className="font-display text-[22px] leading-tight text-[color:var(--maroon)] line-clamp-1" title={v.name}>{v.name}</h3>
            <div className="text-[13px] text-neutral-500 mt-1 inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-emerald-600"/> Verified · {v.city}{v.state ? `, ${v.state}` : ""}</div>
          </div>
          {!compact && <p className="text-[14px] text-neutral-600 leading-relaxed line-clamp-2 min-h-[44px]">{v.description}</p>}
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[13px] text-neutral-700 min-h-[20px]">
            {cap && v.pax_max > 0 && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[color:var(--maroon)]"/> {v.pax_min}–{v.pax_max} guests</span>}
            {cap && v.veg_options && <span className="inline-flex items-center gap-1"><Leaf className="w-3.5 h-3.5 text-emerald-600"/> Veg</span>}
            {cap && v.non_veg_options && <span className="inline-flex items-center gap-1"><Drumstick className="w-3.5 h-3.5 text-rose-600"/> Non-veg</span>}
            {isFood(v.category) && v.parallel_slots > 1 && <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-[color:var(--maroon)]"/> {v.parallel_slots} events/day</span>}
            {v.views > 0 && <span className="inline-flex items-center gap-1 text-neutral-400"><Eye className="w-3.5 h-3.5"/> {v.views}</span>}
          </div>
          {!compact && (
            <div className="flex flex-wrap gap-1.5 content-start min-h-[56px] max-h-[56px] overflow-hidden">
              {(v.amenities || []).slice(0, 3).map((a) => <span key={a} className="text-[12px] px-2 py-0.5 rounded-full bg-[color:var(--ivory)] text-[color:var(--ink-2)] max-w-[160px] truncate">{a}</span>)}
              {(v.amenities || []).length > 3 && <span className="text-[12px] px-2 py-0.5 rounded-full bg-[color:var(--ivory)] text-neutral-500">+{v.amenities.length - 3} more</span>}
            </div>
          )}
          {!compact && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-neutral-500 min-h-[18px] max-h-[18px] overflow-hidden">
              {v.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/> {v.contact_phone}</span>}
              {v.contact_email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3"/> {v.contact_email}</span>}
            </div>
          )}
          <div className="mt-auto pt-3 flex items-center justify-between border-t border-[color:var(--line)]/70">
            <div>
              <span className="text-neutral-500 text-[12px]">from </span>
              <span className="font-num text-[color:var(--maroon)] text-[19px]">₹{Number(p.amount || 0).toLocaleString("en-IN")}</span>
              <span className="text-[12px] text-neutral-500"> {p.unit}</span>
            </div>
            <span className="text-[13px] text-[color:var(--maroon)] font-semibold group-hover:underline underline-offset-4">View details</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function CardImage({ v }) {
  const [step, setStep] = useState(0); // 0: listing photo, 1: category photo, 2: tile
  const own = mediaUrl(v.images?.[0]);
  const cat = CAT[v.category]?.img;
  const src = step === 0 ? (own || cat) : step === 1 ? cat : null;
  if (!src) {
    const I = CAT[v.category]?.Icon || Sparkles;
    return <div className="w-full h-full flex items-center justify-center bg-[color:var(--maroon-2)] rings"><I className="w-10 h-10 text-[color:var(--gold-2)]"/></div>;
  }
  return <img src={src} alt={v.name} loading="lazy" onError={() => setStep((s) => (s === 0 && own && cat ? 1 : 2))} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>;
}

export function VenueCardSkeleton() {
  return (
    <div className="card-luxury overflow-hidden">
      <div className="aspect-[4/3] skeleton"/>
      <div className="p-5 space-y-3">
        <div className="h-5 w-3/4 skeleton"/>
        <div className="h-3 w-1/2 skeleton"/>
        <div className="h-3 w-1/3 skeleton"/>
      </div>
    </div>
  );
}
